import { X, Info } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import TestRow from '../components/TestRow';
import { setTestViewer, setImagePredictionMenu, setImageViewer } from '../Redux/store';
import { formatDateTime, formatDateLong } from '../utils/dateFormat';

/** Get cycle context message and color classes based on test value and cycle day */
function getCycleContext(testValue, cycleDay) {
  if (testValue == null) {
    return { message: 'Analyzing your test...', colors: { text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' } };
  }

  const value = Number(testValue);

  // AI failed to detect anything
  if (value === -1) {
    return { message: 'Could not analyze test. Please retake the photo.', colors: { text: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30' } };
  }

  if (value > 0.9) {
    return { message: 'LH level very high. This may be your peak reading.', colors: { text: 'text-purple-700', bg: 'bg-purple-700/25', border: 'border-purple-700/60' } };
  }

  // High LH level
  if (value > 0.7) {
    return { message: 'LH level High', colors: { text: 'text-purple-600', bg: 'bg-purple-600/20', border: 'border-purple-600/50' } };
  }

  // Medium LH level
  if (value > 0.45 && value <= 0.7) {
    return { message: 'Medium - peak may come soon. Test twice per day', colors: { text: 'text-purple-500', bg: 'bg-purple-500/15', border: 'border-purple-500/40' } };
  }

  // Low/medium LH level
  if (value >= 0.25 && value <= 0.45) {
    return { message: 'Low/medium LH - it may be rising', colors: { text: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' } };
  }

  // Low fertility
  if (value < 0.25) {
    if (cycleDay && cycleDay < 10) {
      return { message: 'LH levels are currently low. Its still early in your cycle.', colors: { text: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' } };
    }
    if (cycleDay && cycleDay >= 10 && cycleDay < 14) {
      return { message: 'LH levels are currently low. Ovulation may be coming in a few days.', colors: { text: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' } };
    }
    if (cycleDay && cycleDay >= 14 && cycleDay < 18) {
      return { message: 'LH levels are currently low. You may have already ovulated.', colors: { text: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' } };
    }
    return { message: 'LH levels are currently low. Continue testing daily.', colors: { text: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' } };
  }

  return { message: 'Test result received.', colors: { text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' } };
}

export default function TestResultMenu({ cycleDay }) {
  const dispatch = useDispatch();
  const calendarData = useSelector((s) => s.calendarDataSelf.data);
  const calendarDataProcessed = useSelector((s) => s.calendarDataSelf.dataProcessed);
  const selfTests = useSelector((s) => s.ovulationTests.selfTests);
  const testViewer = useSelector((s) => s.ui.testViewer);

  if (!testViewer) return null;

  const { test, uploading } = testViewer;

  // If not uploading and we have a test ID, try to get the latest data from Redux
  let testFromRedux = null;
  if (!uploading && test?.id && test?.taken_at) {
    // First check ovulationTests.selfTests (has prediction_data)
    testFromRedux = selfTests?.find(t => t.id === test.id);
    
    // If not found, check calendarData
    if (!testFromRedux && calendarData) {
      const localDate = new Date(test.taken_at);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;
      if (calendarData[dateKey]?.ovulationTests) {
        testFromRedux = calendarData[dateKey].ovulationTests.find(t => t.id === test.id);
      }
    }
  }

  // Use Redux data if available, or original test
  const displayTest = testFromRedux ? { ...testFromRedux, fileUrl: test?.fileUrl } : test;
  const { message: cycleContext, colors: cycleColors } = getCycleContext(displayTest.value, cycleDay);

  // Use calendarDataProcessed for cycle day lookup if calendarData is empty
  const calendarDataForCD = calendarDataProcessed || calendarData;

  // Get taken_at from multiple possible field names
  const takenAt = displayTest?.taken_at || displayTest?.takenAt || displayTest?.startTime;

  const handleClose = () => {
    dispatch(setTestViewer(null));
  };

  const handleImageClick = (imageData) => {
    dispatch(setImageViewer({ images: [imageData], initialIndex: 0 }));
  };

  const handlePredictionInfoClick = () => {
    if (displayTest?.prediction_data) {
      dispatch(setImagePredictionMenu({ test: displayTest, predictionData: displayTest.prediction_data }));
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ paddingBottom: '80px' }}>
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-[90%] max-w-md flex flex-col rounded-3xl bg-card border border-primary shadow-xl overflow-hidden" style={{ maxHeight: 'calc(90vh - 72px)' }}>
        <button onClick={handleClose} className="absolute right-4 top-4 z-10 text-muted-foreground">
          <X className="h-5 w-5" />
        </button>
        <div className="bg-gradient-to-br from-primary to-accent p-6 text-primary-foreground shrink-0">
          <h2 className="text-2xl font-bold">Test Result</h2>
          {takenAt && (
            <p className="text-sm opacity-90 mt-1">{formatDateLong(takenAt)}</p>
          )}
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {/* Test row */}
          {displayTest && ( 
            <div className="mb-4">
              <TestRow test={displayTest} allTests={[displayTest]} uploading={uploading} onImageClick={handleImageClick} calendarData={calendarDataForCD}/>
            </div>
          )}

          {/* Cycle context message with color coding */}
          {cycleContext && (
            <div className={`mt-4 p-4 rounded-xl ${cycleColors.bg} border ${cycleColors.border} text-center relative`}>
              {/* Info button for prediction data */}
              {displayTest?.prediction_data && (
                <button
                  onClick={handlePredictionInfoClick}
                  className="absolute rounded-[50%] bg-background/50 flex items-center justify-center transition-colors hover:bg-background/80"
                  style={{ top: '2px', right: '2px', height: '20px', width: '20px', padding: '2px' }}
                >
                  <div className="h-full w-full rounded-[50%] border border-black flex items-center justify-center">
                    <span className="text-[7px] font-bold text-foreground" style={{ marginTop: '1px' }}>AI</span>
                  </div>
                </button>
              )}
              <p className={`text-sm font-medium ${cycleColors.text}`}>{cycleContext}</p>
            </div>
          )}
        </div>
        <div className="p-4 pt-0 shrink-0">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground shadow disabled:opacity-50"
            style={{ marginTop: '5px' }}
          >
            {uploading ? 'Uploading...' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}
